import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi, type PingSandboxCheckResult } from '../services/api';

const errorKeys: Record<string, string> = {
  PING_SANDBOX_CONFIGURATION_MISSING: 'configurationMissing',
  PING_SANDBOX_CONFIGURATION_INVALID: 'configurationInvalid',
  PING_SANDBOX_AUTH_FAILED: 'authentication',
  PING_SANDBOX_TIMEOUT: 'timeout',
  PING_SANDBOX_UNEXPECTED_RESPONSE: 'unexpectedResponse',
  PING_SANDBOX_PRODUCTION_DISABLED: 'production',
};

export function PingSandboxCheck({ enabled }: { enabled: boolean }) {
  const { t, i18n } = useTranslation();
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<PingSandboxCheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const check = async () => {
    setChecking(true);
    setResult(null);
    setError(null);
    try {
      setResult(await adminApi.checkPingSandbox());
    } catch (caught) {
      const data = (caught as { response?: { data?: { error?: { code?: string } } } })?.response?.data;
      setError(errorKeys[data?.error?.code ?? ''] ?? 'unavailable');
    } finally { setChecking(false); }
  };

  return <section aria-labelledby="ping-sandbox-title">
    <h5 id="ping-sandbox-title">{t('pingSandbox.title')}</h5>
    <p className="text-muted">{t('pingSandbox.description')}</p>
    {!enabled && <p className="text-muted">{t('pingSandbox.errors.production')}</p>}
    <button type="button" className="btn btn-outline-primary btn-lg" disabled={!enabled || checking} onClick={() => void check()}>
      {t(checking ? 'pingSandbox.checking' : 'pingSandbox.check')}
    </button>
    {checking && <p className="mt-3 mb-0" role="status">{t('pingSandbox.checking')}</p>}
    {error && <div className="alert alert-danger mt-3 mb-0" role="alert">{t(`pingSandbox.errors.${error}`)}</div>}
    {result && <div className="alert alert-success mt-3 mb-0" role="status">
      <p className="mb-1 fw-semibold">{t('pingSandbox.success')}</p>
      <p className="mb-1">{t('pingSandbox.checkedAt', { time: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'medium' }).format(new Date(result.checkedAt)) })}</p>
      <p className="mb-0">{t('pingSandbox.scope')}</p>
    </div>}
  </section>;
}
